import { FormEvent, useCallback, useEffect, useMemo, useState } from "react";
import { BridgePairingState, Mt5Status, api, isError } from "../api";

export function TradingAccountPage() {
  const [status, setStatus] = useState<Mt5Status | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    text: string;
    tone: "ok" | "warn" | "error";
  } | null>(null);
  const [busy, setBusy] = useState(false);

  // Draft inputs — login + server prefill from current state so the
  // operator can change just one field without re-typing everything.
  const [draftLogin, setDraftLogin] = useState("");
  const [draftPassword, setDraftPassword] = useState("");
  const [draftServer, setDraftServer] = useState("");
  const [reveal, setReveal] = useState(false);

  const refresh = useCallback(async () => {
    const r = await api.getMt5();
    if (isError(r)) {
      setLoadError(r.error);
      return;
    }
    setLoadError(null);
    setStatus(r.mt5);
    // Prefill the form with what's saved so the user doesn't need to
    // re-type to e.g. just swap the password.
    setDraftLogin(r.mt5.login != null ? String(r.mt5.login) : "");
    setDraftServer(r.mt5.server || "");
    // Never prefill the password — even though we have it, the UI
    // wants the operator to type-confirm a change.
    setDraftPassword("");
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setMessage(null);

    if (status?.bridge_connected && !window.confirm(
      `Replace the active MT5 connection (currently ${status.bridge_login}@${status.bridge_server})? ` +
      "The bridge will briefly drop the broker connection while it re-initialises with the new credentials. " +
      "Open positions are NOT closed.",
    )) {
      return;
    }

    setBusy(true);
    const login = Number(draftLogin);
    if (!Number.isFinite(login) || login <= 0) {
      setMessage({ text: "Login must be a positive integer.", tone: "error" });
      setBusy(false);
      return;
    }
    const r = await api.setMt5(login, draftPassword, draftServer.trim());
    setBusy(false);
    if (isError(r)) {
      setMessage({ text: r.error, tone: "error" });
      void refresh();
      return;
    }
    setStatus(r.mt5);
    setDraftPassword("");
    if (r.pushed) {
      setMessage({
        text: "Saved and pushed to the bridge. MT5 re-initialised successfully.",
        tone: "ok",
      });
    } else if (r.bridge_error) {
      setMessage({
        text: `Saved. Bridge push failed: ${r.bridge_error}. Will retry on next bridge reconnect, or click Save again once the bridge is online.`,
        tone: "warn",
      });
    } else {
      setMessage({
        text: "Saved. (No bridge connected to push to.)",
        tone: "warn",
      });
    }
  }

  async function clear() {
    if (!window.confirm(
      "Clear the saved MT5 credentials? The bridge keeps its current connection — restart the bridge process to fall back to env values."
    )) {
      return;
    }
    setBusy(true);
    setMessage(null);
    const r = await api.clearMt5();
    setBusy(false);
    if (isError(r)) {
      setMessage({ text: r.error, tone: "error" });
      void refresh();
      return;
    }
    setStatus(r.mt5);
    setMessage({
      text: `Cleared ${r.cleared} field${r.cleared === 1 ? "" : "s"}. Bridge keeps its current connection.`,
      tone: "ok",
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <header className="flex items-baseline justify-between">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Trading account</h1>
          <p className="text-xs text-fg-muted">
            Pair the local MT5 bridge, then enter the broker login, password,
            and server. Credentials are pushed to the bridge over its
            WebSocket and applied in place — no .env, no bridge restart.
          </p>
        </div>
      </header>

      {loadError && (
        <p className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          Couldn&apos;t load MT5 status: {loadError}
        </p>
      )}

      {message && (
        <p
          className={`rounded-md px-3 py-2 text-sm ${
            message.tone === "ok"
              ? "border border-success/40 bg-success/10 text-success"
              : message.tone === "warn"
                ? "border border-warn/40 bg-warn/10 text-warn"
                : "border border-danger/40 bg-danger/10 text-danger"
          }`}
        >
          {message.text}
        </p>
      )}

      {status === null && !loadError ? (
        <p className="text-sm text-fg-muted">Loading…</p>
      ) : (
        <>
          <BridgePairingCard bridgeConnected={!!status?.bridge_connected} />

          {status?.bridge_connected && status.bridge_login == null && (
            <p className="rounded-md border border-warn/40 bg-warn/10 px-3 py-2 text-sm text-warn">
              The bridge is connected but has no MT5 account yet. Enter the
              broker credentials below — they are pushed straight to it.
            </p>
          )}

          {/* Status card */}
          <section className="rounded-xl border border-border bg-surface p-5">
            <h2 className="mb-3 font-mono text-sm font-semibold uppercase tracking-wider text-fg-muted">
              Current connection
            </h2>
            <dl className="grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 text-sm">
              <dt className="text-fg-muted">Bridge</dt>
              <dd>
                <span
                  className={`inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs font-medium ${
                    status?.bridge_connected
                      ? "bg-success/15 text-success"
                      : "bg-danger/15 text-danger"
                  }`}
                >
                  <span
                    className={`h-1.5 w-1.5 rounded-full ${
                      status?.bridge_connected ? "bg-success" : "bg-danger"
                    }`}
                  />
                  {status?.bridge_connected ? "connected" : "offline"}
                </span>
              </dd>
              <dt className="text-fg-muted">MT5 login</dt>
              <dd className="font-mono">
                {status?.bridge_login != null
                  ? status.bridge_login
                  : status?.login != null
                    ? `${status.login} (saved, ${status.bridge_connected ? "not applied yet" : "bridge offline"})`
                    : "—"}
              </dd>
              <dt className="text-fg-muted">MT5 server</dt>
              <dd className="font-mono">
                {status?.bridge_server ?? status?.server ?? "—"}
              </dd>
              <dt className="text-fg-muted">Credentials source</dt>
              <dd>
                {status?.is_user_set ? (
                  <span className="rounded bg-success/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-success">
                    UI-saved
                  </span>
                ) : status?.has_env_fallback ? (
                  <span className="rounded bg-warn/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-warn">
                    from env
                  </span>
                ) : (
                  <span className="rounded bg-bg-hover px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-fg-muted">
                    not set
                  </span>
                )}
              </dd>
            </dl>
          </section>

          {/* Edit form */}
          <section className="rounded-xl border border-border bg-surface p-5">
            <h2 className="mb-3 font-mono text-sm font-semibold uppercase tracking-wider text-fg-muted">
              {status?.login != null ? "Replace credentials" : "MT5 credentials"}
            </h2>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-[max-content_1fr] items-center gap-x-4 gap-y-3">
                <label htmlFor="mt5-login" className="text-sm font-medium text-fg">
                  Login
                </label>
                <input
                  id="mt5-login"
                  type="number"
                  value={draftLogin}
                  onChange={(e) => setDraftLogin(e.target.value)}
                  placeholder="5023412"
                  className="w-48 rounded-md border border-border bg-page px-2 py-1 text-right font-mono text-sm tabular-nums text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                  disabled={busy}
                  required
                />

                <label htmlFor="mt5-server" className="text-sm font-medium text-fg">
                  Server
                </label>
                <input
                  id="mt5-server"
                  type="text"
                  value={draftServer}
                  onChange={(e) => setDraftServer(e.target.value)}
                  placeholder="Exness-MT5Trial7"
                  className="w-80 rounded-md border border-border bg-page px-2 py-1 font-mono text-sm text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                  disabled={busy}
                  required
                />

                <label htmlFor="mt5-password" className="text-sm font-medium text-fg">
                  Trading password
                </label>
                <div className="flex items-center gap-2">
                  <input
                    id="mt5-password"
                    type={reveal ? "text" : "password"}
                    value={draftPassword}
                    onChange={(e) => setDraftPassword(e.target.value)}
                    placeholder={status?.has_password ? "•••••••• (re-enter to change)" : ""}
                    autoComplete="off"
                    className="w-80 rounded-md border border-border bg-page px-2 py-1 font-mono text-sm text-fg outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                    disabled={busy}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setReveal((v) => !v)}
                    className="rounded-md border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg"
                  >
                    {reveal ? "hide" : "show"}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2 pt-2">
                <button
                  type="submit"
                  disabled={busy || !draftLogin || !draftPassword || !draftServer}
                  className="rounded-md bg-primary px-4 py-2 text-sm font-semibold uppercase tracking-wide text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {busy ? "saving…" : status?.bridge_connected ? "save + reconnect" : "save"}
                </button>
                {status?.is_user_set && (
                  <button
                    type="button"
                    onClick={() => void clear()}
                    className="rounded-md border border-border px-3 py-2 text-xs uppercase tracking-wide text-fg-muted hover:bg-danger/10 hover:text-danger"
                    disabled={busy}
                  >
                    clear saved credentials
                  </button>
                )}
              </div>
            </form>
            <p className="mt-4 text-xs leading-relaxed text-fg-muted">
              Use the <strong>trading</strong> password, not the investor password.
              MT5 must be installed on the machine running the bridge.{" "}
              Server name must match what your broker shows in MT5 — character-
              perfect (e.g. <code className="font-mono">Exness-MT5Trial7</code>).
              On Exness Standard / Pro accounts the server starts with
              <code className="font-mono"> Exness-MT5Trial*</code> for demo and{" "}
              <code className="font-mono">Exness-MT5Real*</code> for live.
            </p>
          </section>
        </>
      )}
    </div>
  );
}


/** Bridge pairing + token management. The bridge process runs on the
 *  Windows box next to MT5 and dials in here; pairing is how it learns the
 *  token without anyone editing a .env. */
function BridgePairingCard({ bridgeConnected }: { bridgeConnected: boolean }) {
  const [state, setState] = useState<BridgePairingState | null>(null);
  const [code, setCode] = useState<{ value: string; expiresAt: number } | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [token, setToken] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showManual, setShowManual] = useState(false);

  const refresh = useCallback(async () => {
    const r = await api.bridgePairing();
    if (isError(r)) {
      setError(r.error);
      return;
    }
    setError(null);
    setState(r);
    if (!r.pairing_active) setCode(null);
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Tick once a second while a code is live, both for the countdown and
  // so the card flips to "paired" as soon as the bridge shows up.
  useEffect(() => {
    if (!code) return;
    const id = window.setInterval(() => {
      setNow(Date.now());
      void refresh();
    }, 1_000);
    return () => window.clearInterval(id);
  }, [code, refresh]);

  useEffect(() => {
    if (code && bridgeConnected) setCode(null);
  }, [bridgeConnected, code]);

  // The URL the bridge should dial. window.location.origin is what the
  // browser reached the dashboard on, which is exactly what the bridge
  // machine should use too — unless this is the Vite dev server, whose
  // origin the bridge can't use for WebSockets.
  const backendUrl = useMemo(() => {
    if (typeof window === "undefined") return "";
    const o = window.location.origin;
    return /:5173$/.test(o) ? "http://127.0.0.1:8000" : o;
  }, []);

  const secondsLeft = code ? Math.max(0, Math.ceil((code.expiresAt - now) / 1000)) : 0;
  // `bridge` is the repo's bridge.cmd/bridge.ps1 launcher, which resolves
  // the project venv itself. It works from any folder once the fx-dash
  // folder is on PATH (`.\bridge.ps1 --install-path`, once per machine).
  const command = code
    ? `bridge --pair ${code.value} --backend ${backendUrl}`
    : "";

  async function createCode() {
    setBusy(true);
    setError(null);
    const r = await api.bridgeCreatePairing();
    setBusy(false);
    if (isError(r)) {
      setError(r.error);
      return;
    }
    setCode({ value: r.code, expiresAt: Date.now() + r.expires_in * 1000 });
    setNow(Date.now());
    setState(r);
  }

  async function cancelCode() {
    setBusy(true);
    const r = await api.bridgeCancelPairing();
    setBusy(false);
    if (!isError(r)) {
      setState(r);
      setCode(null);
    }
  }

  async function revealToken() {
    setBusy(true);
    const r = await api.bridgeToken();
    setBusy(false);
    if (isError(r)) {
      setError(r.error);
      return;
    }
    setToken(r.value);
  }

  async function rotateToken() {
    if (
      !window.confirm(
        "Rotate the bridge token? The running bridge keeps its current connection, " +
          "but the next time it reconnects it will be refused until you pair it again.",
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    const r = await api.bridgeRotateToken();
    setBusy(false);
    if (isError(r)) {
      setError(r.error);
      return;
    }
    setToken(null);
    setState(r);
  }

  return (
    <section className="rounded-xl border border-border bg-surface p-5">
      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="font-mono text-sm font-semibold uppercase tracking-wider text-fg-muted">
          MT5 bridge
        </h2>
        <span
          className={`inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-xs font-medium ${
            bridgeConnected ? "bg-success/15 text-success" : "bg-danger/15 text-danger"
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${bridgeConnected ? "bg-success" : "bg-danger"}`}
          />
          {bridgeConnected ? "connected" : "not connected"}
        </span>
      </div>

      {error && (
        <p className="mb-3 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">
          {error}
        </p>
      )}

      {bridgeConnected && !code ? (
        <p className="text-sm text-fg-muted">
          The bridge is paired and online. To move it to another machine,
          generate a new pairing code and run the command there.
        </p>
      ) : (
        <p className="text-sm text-fg-muted">
          Run the bridge on the Windows machine that has MetaTrader 5
          installed. One-time setup there: open PowerShell in the{" "}
          <code className="font-mono">fx-dash</code> folder and run{" "}
          <code className="font-mono">{".\\bridge.ps1 --install-path"}</code>{" "}
          (puts the launcher on your PATH). After pairing, every later start is
          just <code className="font-mono">bridge</code> from any folder.
        </p>
      )}

      {code ? (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-3">
            <span className="rounded-md border border-border bg-page px-3 py-1.5 font-mono text-lg tracking-[0.25em]">
              {code.value}
            </span>
            <span className="text-xs text-fg-muted">
              {secondsLeft > 0 ? `expires in ${secondsLeft}s` : "expired"}
            </span>
            <button
              type="button"
              onClick={() => void cancelCode()}
              disabled={busy}
              className="rounded-md border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg"
            >
              cancel
            </button>
          </div>
          <p className="text-xs text-fg-muted">
            In a <strong>new</strong> PowerShell window on the bridge machine:
          </p>
          <CopyRow value={command} />
          <p className="text-xs text-fg-muted">
            Waiting for the bridge to connect… this card updates automatically.
          </p>
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={() => void createCode()}
            disabled={busy}
            className="rounded-md bg-primary px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-white transition hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? "…" : bridgeConnected ? "pair another bridge" : "pair bridge"}
          </button>
          <button
            type="button"
            onClick={() => setShowManual((v) => !v)}
            className="rounded-md border border-border px-2 py-1.5 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg"
          >
            {showManual ? "hide advanced" : "advanced"}
          </button>
        </div>
      )}

      {showManual && !code && (
        <div className="mt-4 space-y-2 border-t border-border pt-3">
          <p className="text-xs text-fg-muted">
            Manual setup: pass the token directly with{" "}
            <code className="font-mono">{"bridge --token <TOKEN> --backend "}{backendUrl}</code>.
            Rotating invalidates every bridge until it is paired again.
          </p>
          <div className="flex items-center gap-2">
            {token == null ? (
              <button
                type="button"
                onClick={() => void revealToken()}
                disabled={busy || !state?.token_configured}
                className="rounded-md border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-50"
              >
                reveal token
              </button>
            ) : (
              <div className="flex-1">
                <CopyRow value={token} />
              </div>
            )}
            <button
              type="button"
              onClick={() => void rotateToken()}
              disabled={busy}
              className="rounded-md border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-danger/10 hover:text-danger disabled:opacity-50"
            >
              rotate token
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

function CopyRow({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API can fail in non-secure contexts; the field is
      // selectable so the operator can copy manually.
    }
  }

  return (
    <div className="flex items-center gap-2">
      <input
        type="text"
        readOnly
        value={value}
        onFocus={(e) => e.currentTarget.select()}
        className="min-w-0 flex-1 rounded-md border border-border bg-page px-2 py-1 font-mono text-sm text-fg outline-none"
      />
      <button
        type="button"
        onClick={() => void copy()}
        className="rounded-md border border-border px-2 py-1 text-[10px] uppercase tracking-wide text-fg-muted hover:bg-bg-hover hover:text-fg"
      >
        {copied ? "copied" : "copy"}
      </button>
    </div>
  );
}
